import React, { useState, useEffect } from "react";
import { Dropdown, Button, Spinner, Alert } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import {
  addContent,
  updateContent,
  deleteContent,
  addPlanToRoom,
  updatePlanInRoom,
  deletePlanFromRoom,
  uploadImage,
  getPhysicalRooms,
  syncPhysicalRooms,
  deletePhysicalRoomsByHotelRoom,
} from "../../../FIREBASECONFIG";
import {
  fetchHotelRoomsByHotelId,
  fetchRoomPlans,
  invalidateResortCache,
} from "../../../api/resort-booking/resort.service";
import { AMENITY_OPTIONS } from "./Hotels";

const buildPhysicalRoomRows = (quantity, previous = []) => {
  const qty = Math.max(0, Number(quantity) || 0);
  if (previous.length === qty) return previous;
  if (previous.length < qty) {
    const added = Array.from({ length: qty - previous.length }, () => ({
      label: "",
      status: "available",
    }));
    return [...previous, ...added];
  }
  return previous.slice(0, qty);
};

/** Every block of the room form sits in its own card, like the booking detail screens. */
const FormSection = ({ title, description, action, children }) => (
  <div className="col-12">
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="card-title m-0">{title}</h5>
            {description ? <small className="text-muted">{description}</small> : null}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  </div>
);

const ResortRooms = ({ hotel }) => {
  const collectionName = "hotel_rooms";
  const imageFolder = "Room_Images";
  const [rooms, setRooms] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const itemsPerPage = 10;

  // UI State
  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [formData, setFormData] = useState({});
  const [gallery, setGallery] = useState([]); // [{url, alt, file}]
  const [plans, setPlans] = useState([]);
  const [physicalRooms, setPhysicalRooms] = useState([]);

  // 🔹 Fetch Rooms
  const fetchRooms = async () => {
    try {
      setLoading(true);
      const filtered = await fetchHotelRoomsByHotelId(hotel.id);
      setRooms(filtered);
    } catch (error) {
      setAlert({ type: "danger", message: "Error fetching rooms." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [hotel]);

  // Keep physical room rows in sync with quantity (totalRoom)
  useEffect(() => {
    if (!showForm) return;
    setPhysicalRooms((prev) => buildPhysicalRoomRows(formData.totalRoom, prev));
  }, [formData.totalRoom, showForm]);

  // 🔹 Form Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleAmenity = (key) => {
    setFormData((prev) => {
      const current = prev.facilities || [];
      return {
        ...prev,
        facilities: current.includes(key)
          ? current.filter((a) => a !== key)
          : [...current, key],
      };
    });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditRoom(null);
    setFormData({});
    setGallery([]);
    setPlans([]);
    setPhysicalRooms([]);
  };

  // 🔹 Save Room + Plans + Physical Rooms
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const quantity = Number(formData.totalRoom) || 0;
      const roomRows = buildPhysicalRoomRows(quantity, physicalRooms);

      if (quantity < 1) {
        setAlert({
          type: "danger",
          message: "Quantity must be at least 1.",
        });
        return;
      }

      const missingLabelIndex = roomRows.findIndex(
        (row) => !String(row.label || "").trim()
      );
      if (missingLabelIndex !== -1) {
        setAlert({
          type: "danger",
          message: `All physical rooms are required. Please enter a label for room row ${
            missingLabelIndex + 1
          }.`,
        });
        return;
      }

      // 🔹 Upload images from gallery
      let uploadedImages = [];
      for (const g of gallery) {
        if (g.file) {
          const uploadedUrl = await uploadImage(g.file, imageFolder);
          uploadedImages.push({ url: uploadedUrl, alt: g.alt });
        } else {
          uploadedImages.push({ url: g.url, alt: g.alt });
        }
      }

      // 🔹 Prepare room data
      const newRoom = {
        hotel_id: hotel.id,
        ...formData,
        totalRoom: quantity,
        facilities: formData.facilities || [],
        images: uploadedImages,
      };

      let roomId;
      if (editRoom) {
        await updateContent(collectionName, imageFolder, editRoom.id, newRoom);
        roomId = editRoom.id;
        setAlert({ type: "success", message: "Room updated successfully." });
      } else {
        roomId = await addContent(collectionName, imageFolder, newRoom);
        roomId = roomId.id;
        setAlert({ type: "success", message: "Room added successfully." });
      }

      // 🔹 Save Plans (totalPlan mirrors hotel_rooms.totalRoom)
      const planIds = [];
      for (const plan of plans) {
        const planPayload = {
          ...plan,
          room_id: roomId,
          hotel_id: hotel.id,
          totalPlan: quantity,
        };
        if (plan.id) {
          await updatePlanInRoom(roomId, plan.id, planPayload);
          planIds.push(plan.id);
        } else {
          const newPlanId = await addPlanToRoom(roomId, planPayload);
          planIds.push(newPlanId);
        }
      }

      // 🔹 Sync physical rooms collection
      await syncPhysicalRooms({
        hotelId: hotel.id,
        hotelRoomId: roomId,
        planIds,
        rooms: roomRows.map((row) => ({
          ...row,
          label: String(row.label).trim(),
        })),
      });

      invalidateResortCache.afterHotelRoomChange();
      invalidateResortCache.afterRoomPlanChange(roomId);
      resetForm();
      fetchRooms();
    } catch (error) {
      console.error(error);
      setAlert({ type: "danger", message: "Error saving room." });
    }
  };

  // 🔹 Delete Room
  const handleDeleteRoom = async (id) => {
    try {
      await deletePhysicalRoomsByHotelRoom(id);
      await deleteContent(collectionName, id);
      invalidateResortCache.afterHotelRoomChange();
      invalidateResortCache.afterRoomPlanChange(id);
      setRooms((prev) => prev.filter((c) => c.id !== id));
      setAlert({ type: "success", message: "Room deleted successfully." });
    } catch (error) {
      setAlert({ type: "danger", message: "Error deleting room." });
    }
  };

  // 🔹 Images
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10);
    const mapped = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f), // preview URL
      alt: "",
    }));
    setGallery((prev) => [...prev, ...mapped]);
  };

  const handleRemoveImage = (idx) => {
    setGallery((prev) => prev.filter((_, i) => i !== idx));
  };

  // 🔹 Plans
  const addPlan = () => {
    setPlans([
      ...plans,
      { name: "", maxPerson: "", refundable: false, stopSell: false },
    ]);
  };

  const updatePlan = (index, field, value) => {
    const updated = [...plans];
    updated[index][field] = value;
    setPlans(updated);
  };

  const deletePlan = async (index) => {
    const plan = plans[index];
    if (plan.id && editRoom) {
      await deletePlanFromRoom(editRoom.id, plan.id);
      invalidateResortCache.afterRoomPlanChange(editRoom.id);
    }
    setPlans(plans.filter((_, i) => i !== index));
  };

  const updatePhysicalRoom = (index, field, value) => {
    setPhysicalRooms((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // 🔹 Edit Room (load its plans + physical rooms)
  const handleEditRoom = async (room) => {
    setEditRoom(room);
    setFormData(room);
    setGallery(
      room.images?.map((img) => ({
        url: img.url || img,
        alt: img.alt || "",
        file: null,
      })) || []
    );

    const [roomPlans, units] = await Promise.all([
      fetchRoomPlans(room.id),
      getPhysicalRooms(room.id, hotel?.id || room.hotel_id),
    ]);
    setPlans(roomPlans);

    // Prefer unique labels, then trim to quantity so duplicate docs don't
    // inflate the form (sync will delete leftovers on save).
    const seenLabels = new Set();
    const uniqueUnits = [];
    for (const unit of units) {
      const key = String(unit.label || "").trim().toLowerCase();
      if (key && seenLabels.has(key)) continue;
      if (key) seenLabels.add(key);
      uniqueUnits.push(unit);
    }
    setPhysicalRooms(
      buildPhysicalRoomRows(
        room.totalRoom,
        uniqueUnits.length ? uniqueUnits : []
      )
    );
    setShowForm(true);
  };

  // 🔹 Filter + Paginate
  const filteredRooms = rooms.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const paginatedRooms = filteredRooms.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const handleToggle = async (room) => {
    try {
      const updated = { ...room, isApproved: !room.isApproved };
      await updateContent(collectionName, imageFolder, room.id, updated);
      invalidateResortCache.afterHotelRoomChange();
      setRooms((prev) => prev.map((r) => (r.id === room.id ? updated : r)));
      setAlert({ type: "success", message: `Room ${updated.isApproved ? "approved" : "unapproved"} successfully` });
    } catch (e) {
      console.error("Error updating Status:", e);
      setAlert({ type: "warning", message: `Error Updating IsApproved Status` });
    }
  };

  return (
    <div className="col-12">
       {alert && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
              {alert.message}
            </Alert>
        )}
      <div>
        {/*---------- Header------------ */}
        <div className="d-flex justify-content-between align-items-center gap-2 mb-4">
          <h4 className="card-title m-0">
            {showForm ? (editRoom ? "Edit Room" : "Add Room") : "Hotel Rooms"}
          </h4>
          {!showForm && (
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search room..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  setEditRoom(null);
                  setFormData({});
                  setGallery([]);
                  setPlans([]);
                  setPhysicalRooms([]);
                  setShowForm(true);
                }}
                style={{ whiteSpace: "nowrap" }}
              >
                Add Room
              </Button>
            </div>
          )}
        </div>
        {/* Only the list keeps a card around it: the form brings its own. */}
        <div className={showForm ? "" : "card p-4"}>
          {showForm ? (
            // 🔹 Room Form
            <form onSubmit={handleSubmit}>
              <div className="row">
                <FormSection title="Room Details">
                  <div className="row">
                    <div className="col-md-5 mb-3">
                      <label className="form-label">Room Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Quantity</label>
                      <input
                        type="number"
                        name="totalRoom"
                        min="0"
                        value={formData.totalRoom || ""}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="col-md-3 mb-3 d-flex align-items-end">
                      <div className="form-check form-switch mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="isApproved"
                          style={{ cursor: "pointer" }}
                          checked={formData.isApproved || false}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Enabled</label>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        rows={3}
                        value={formData.description || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Room description..."
                      />
                    </div>
                  </div>
                </FormSection>

                {/* Amenities */}
                <FormSection title="Facilities">
                  <div className="d-flex flex-wrap gap-2">
                    {AMENITY_OPTIONS.map(({ key, label, Icon }) => {
                      const active = formData.facilities?.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${active ? "btn-primary" : "btn-outline-primary"} d-flex align-items-center gap-2`}
                          onClick={() => toggleAmenity(key)}
                        >
                          <Icon />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </FormSection>

                {/* Images */}
                <FormSection title="Images">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    className="form-control"
                  />
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    {gallery.map((item, idx) => (
                      <div
                        key={idx}
                        className="position-relative border rounded p-1"
                        style={{ width: 130 }}
                      >
                        <img
                          src={item.url}
                          alt={item.alt || `img-${idx}`}
                          className="img-fluid rounded"
                          style={{ width: "100%", height: 80, objectFit: "cover" }}
                        />
                        <button
                          type="button"
                          className="position-absolute border-0 d-flex align-items-center justify-content-center room-image-remove-btn"
                          style={{
                            top: 6,
                            right: 6,
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            cursor: "pointer",
                            padding: 0,
                            zIndex: 2,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                            transition: "background 0.15s ease, color 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(220,53,69,0.95)";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(0,0,0,0.55)";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onClick={() => handleRemoveImage(idx)}
                          title="Remove image"
                        >
                          <FaTrash size={11} />
                        </button>
                        {/* Alt text input hidden for now
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Alt text (required)"
                          required
                          value={item.alt || ""}
                          onChange={(e) => {
                            const updated = [...gallery];
                            updated[idx] = { ...item, alt: e.target.value };
                            setGallery(updated);
                          }}
                        />
                        */}
                      </div>
                    ))}
                  </div>
                </FormSection>

                {/* Plans */}
                <FormSection
                  title="Plans"
                  action={
                    <Button size="sm" onClick={addPlan}>
                      + Add Plan
                    </Button>
                  }
                >
                  {plans.length === 0 ? (
                    <p className="text-muted mb-0">No plans added yet.</p>
                  ) : null}
                  {plans.map((plan, i) => (
                    <div
                      key={i}
                      className="row g-2 align-items-end border rounded p-2 mb-2"
                    >
                      <div className="col-md-4">
                        <label className="form-label">Plan Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Non Refundable"
                          value={plan.name}
                          onChange={(e) => updatePlan(i, "name", e.target.value)}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">No of Pax</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="e.g. 3"
                          value={plan.maxPerson}
                          onChange={(e) =>
                            updatePlan(i, "maxPerson", e.target.value)
                          }
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label d-block">Refundable</label>
                        <div className="form-check form-switch mt-1">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ cursor: "pointer" }}
                            checked={!!plan.refundable}
                            onChange={(e) =>
                              updatePlan(i, "refundable", e.target.checked)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label d-block">Stop Sell</label>
                        <div className="form-check form-switch mt-1">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ cursor: "pointer" }}
                            checked={!!plan.stopSell}
                            onChange={(e) =>
                              updatePlan(i, "stopSell", e.target.checked)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-2 d-flex align-items-end pb-1">
                        <Button
                          variant="danger"
                          size="sm"
                          className="w-100"
                          onClick={() => deletePlan(i)}
                          title="Delete plan"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  ))}
                </FormSection>

                {/* Physical Rooms */}
                <FormSection
                  title="Rooms"
                  description="Row count follows Quantity — rows cannot be added or removed here."
                >
                  {physicalRooms.length === 0 ? (
                    <p className="text-muted mb-0">
                      Set Quantity above to generate room rows.
                    </p>
                  ) : (
                    <>
                      <small className="form-label mb-2 d-block">Room Label</small>
                      <div className="row g-2">
                        {physicalRooms.map((unit, i) => (
                          <div key={unit.id || i} className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter room label"
                              value={unit.label || ""}
                              required
                              onChange={(e) =>
                                updatePhysicalRoom(i, "label", e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </FormSection>

                {/* Buttons */}
                <div className="col-12 d-flex justify-content-end">
                  <button type="submit" className="btn btn-secondary">
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary ms-2"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          ) : loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
              <Spinner animation="border" />
            </div>
          ) : (
            // 🔹 Rooms List
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Room</th>
                    <th>Quantity</th>
                    <th>Enabled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRooms.length > 0 ? (
                    paginatedRooms.map((item, i) => (
                      <tr key={item.id}>
                        <td>{currentPage * itemsPerPage + i + 1}</td>
                        <td>
                          {item.images?.[0] && (
                            <img
                              src={item.images[0].url || item.images[0]}
                              alt={item.images[0].alt || `${item.name}-image`}
                              style={{ width: "60px",height: "40px",  borderRadius: "6px" }}
                            />
                          )}
                        </td>
                        <td>{item.name}</td>
                        <td>{item.totalRoom}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              style={{ cursor: "pointer" }}
                              checked={item.isApproved}
                              onChange={() => handleToggle(item)}
                            />
                          </div>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">&#x2022;&#x2022;&#x2022;</Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => handleEditRoom(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDeleteRoom(item.id)}>
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No rooms found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && !showForm && filteredRooms.length > 0 && (
            <div className="d-flex justify-content-between mt-3">
              <div>
                Showing {currentPage * itemsPerPage + 1} to{" "}
                {Math.min((currentPage + 1) * itemsPerPage, filteredRooms.length)} of{" "}
                {filteredRooms.length} entries
              </div>
              <div>
                <Button
                  size="sm"
                   className="btn btn-sm btn-outline-primary me-2"
                  variant="outline-primary"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Prev
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    className={`ms-1 ${currentPage === i ? "me-2 btn-primary text-white" : "btn-outline-primary"}`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="btn btn-sm btn-outline-primary ms-1"
                  disabled={currentPage === totalPages - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default ResortRooms
